import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Modal from '../components/Modal';
import LoginForm from '../components/LoginForm';
import SignUpPage from './SignUpPage';
import { useAuth } from '../context/AuthContext';

const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const [loginError, setLoginError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    // For now we'll just simulate submission and open user's mail client as fallback
    if (!form.name || !form.email || !form.message) {
      setStatus({ type: 'error', message: 'Please fill all fields.' });
      return;
    }

    const subject = encodeURIComponent('CollabOrb Contact Inquiry');
    const body = encodeURIComponent(`Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`);
    // Try to open mail client by navigating to the mailto: URL directly
    window.location.href = `mailto:support@collaborb.com?subject=${subject}&body=${body}`;
    setStatus({ type: 'success', message: 'Opening your mail client to send the message.' });
  };

  const handleLoginSubmit = async (values) => {
    try {
      setLoginError(''); // Clear previous errors
      const role = await login(values.email, values.password);
      setActiveModal(null);
      if (role.toLowerCase() === 'creator') {
        navigate('/dashboard/creator');
      } else if (role.toLowerCase() === 'brand') {
        navigate('/dashboard/brand');
      }
    } catch (error) {
      setLoginError('Username or password is incorrect');
    }
  };

  return (
    <>
      <Navbar
        onJoinClick={() => setActiveModal('signup')}
        onLoginClick={() => setActiveModal('login')}
      />

      <div className="site-shell pt-20">
        <main className="max-w-4xl mx-auto p-6 sm:p-10 min-h-screen">
          {/* Animated header section */}
          <div className="text-center mb-8 animate-fade-in">
            <h1 className="text-3xl sm:text-4xl font-bold mb-4 text-gray-800 bg-gradient-to-r from-collaborb-band4 to-collaborb-spherical-mid bg-clip-text text-transparent">
              Contact Us
            </h1>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Have questions or need help? Send us a message and we'll get back to you within 1-2 business days.
            </p>
          </div>

          {/* Enhanced form card with hover effect */}
          <div className="app-card rounded-[1.75rem] hover:shadow-2xl transition-all duration-300 p-6 sm:p-8 mb-8 transform hover:-translate-y-1">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="group">
                <label className="block text-sm font-semibold text-gray-700 mb-2 group-focus-within:text-collaborb-band4 transition-colors">
                  Name
                </label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-collaborb-band4 focus:ring-2 focus:ring-collaborb-band4 focus:ring-opacity-20 outline-none transition-all duration-200 hover:border-gray-300"
                />
              </div>

              <div className="group">
                <label className="block text-sm font-semibold text-gray-700 mb-2 group-focus-within:text-collaborb-band4 transition-colors">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                  className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-collaborb-band4 focus:ring-2 focus:ring-collaborb-band4 focus:ring-opacity-20 outline-none transition-all duration-200 hover:border-gray-300"
                />
              </div>

              <div className="group">
                <label className="block text-sm font-semibold text-gray-700 mb-2 group-focus-within:text-collaborb-band4 transition-colors">
                  Message
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={6}
                  placeholder="Tell us how we can help you..."
                  className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-collaborb-band4 focus:ring-2 focus:ring-collaborb-band4 focus:ring-opacity-20 outline-none transition-all duration-200 hover:border-gray-300 resize-none"
                />
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <button
                  type="submit"
                  className="flex-1 bg-collaborb-band4 text-white px-6 py-3 rounded-lg font-semibold hover:bg-collaborb-spherical-mid transform hover:scale-105 active:scale-95 transition-all duration-200 shadow-md hover:shadow-xl"
                >
                  Send Message
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setForm({ name: '', email: '', message: '' });
                    setStatus(null);
                  }}
                  className="px-6 py-3 border-2 border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 transform hover:scale-105 active:scale-95 transition-all duration-200"
                >
                  Reset
                </button>
              </div>

              {status && (
                <div className={`p-4 rounded-lg animate-slide-down ${
                  status.type === 'error' 
                    ? 'bg-red-50 border-l-4 border-red-500 text-red-700' 
                    : 'bg-green-50 border-l-4 border-green-500 text-green-700'
                }`}>
                  <p className="font-medium">{status.message}</p>
                </div>
              )}
            </form>
          </div>

          {/* Contact info cards with hover effects */}
          <section className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-800 text-center mb-6">Get in Touch</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Email card */}
              <a
                href="mailto:support@collaborb.com"
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300 group"
              >
                <div className="flex flex-col items-center text-center space-y-2">
                  <div className="w-12 h-12 bg-collaborb-band4 bg-opacity-10 rounded-full flex items-center justify-center group-hover:bg-opacity-20 transition-colors">
                    <svg className="w-6 h-6 text-collaborb-band4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-700">Email</h3>
                  <p className="text-collaborb-band4 font-medium group-hover:text-collaborb-spherical-mid transition-colors break-all">
                    support@collaborb.com
                  </p>
                </div>
              </a>

              {/* Phone card */}
              <a
                href="tel:+916388178730"
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300 group"
              >
                <div className="flex flex-col items-center text-center space-y-2">
                  <div className="w-12 h-12 bg-collaborb-band4 bg-opacity-10 rounded-full flex items-center justify-center group-hover:bg-opacity-20 transition-colors">
                    <svg className="w-6 h-6 text-collaborb-band4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-700">Phone</h3>
                  <p className="text-collaborb-band4 font-medium group-hover:text-collaborb-spherical-mid transition-colors">
                    +91 6388178730
                  </p>
                </div>
              </a>

              {/* Instagram card */}
              <a
                href="https://www.instagram.com/collaborb/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300 group"
              >
                <div className="flex flex-col items-center text-center space-y-2">
                  <div className="w-12 h-12 bg-collaborb-band4 bg-opacity-10 rounded-full flex items-center justify-center group-hover:bg-opacity-20 transition-colors">
                    <svg className="w-6 h-6 text-collaborb-band4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-700">Instagram</h3>
                  <p className="text-collaborb-band4 font-medium group-hover:text-collaborb-spherical-mid transition-colors">
                    @collaborb
                  </p>
                </div>
              </a>

              {/* Office hours card */}
              <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300 group cursor-default">
                <div className="flex flex-col items-center text-center space-y-2">
                  <div className="w-12 h-12 bg-collaborb-band4 bg-opacity-10 rounded-full flex items-center justify-center group-hover:bg-opacity-20 transition-colors">
                    <svg className="w-6 h-6 text-collaborb-band4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-700">Office Hours</h3>
                  <p className="text-gray-600 font-medium">
                    Mon–Fri<br />9:00 AM – 6:00 PM (IST)
                  </p>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>

      <Modal
        isOpen={activeModal === 'signup'}
        onClose={() => setActiveModal(null)}
        overlayClassName="backdrop-blur-md bg-slate-950/45"
        panelClassName="max-w-xl"
        contentClassName="p-3 pt-12 sm:p-4 sm:pt-12"
      >
        <SignUpPage isOverlay onClose={() => setActiveModal(null)} />
      </Modal>

      <Modal isOpen={activeModal === 'login'} onClose={() => {
        setActiveModal(null);
        setLoginError(''); // Clear error when modal closes
      }}>
        <LoginForm onSubmit={handleLoginSubmit} error={loginError} />
      </Modal>
    </>
  );
};

export default ContactPage;
